/**
 * Fields Components Index
 *
 * Barrel exports and component lookup functions for array field components.
 *
 * Note: The component registries are the source of truth for which fields have
 * custom components. Use getInputComponent(), getOutputComponent(), or
 * getPreviewComponent() to check if a field has a registered component.
 * No separate field lists are maintained - the registry IS the source of truth.
 *
 * Lookups are scoped by workflow type as well as field ID — see the comment on
 * the registries below for why.
 */

import type React from 'react';

import { WorkflowType } from '@/constants/dispositions';

// Import field components for lookup functions
import { DiagnosisInputField, DiagnosisInputFieldPreview } from './DiagnosisInputField';
import { DrugInputField, DrugInputFieldPreview } from './DrugInputField';
import { DrugOutputField, DrugOutputFieldPreview } from './DrugOutputField';
import { ProcedureInputField, ProcedureInputFieldPreview } from './ProcedureInputField';
import { ProcedureOutputField, ProcedureOutputFieldPreview } from './ProcedureOutputField';
import { SiteOfCareInputField } from './SiteOfCareInputField';
import { SiteOfCareOutputField, SiteOfCareOutputFieldPreview } from './SiteOfCareOutputField';
import type { FieldMetadata } from './types';

// Re-export all types
export * from './types';

// Re-export input field components
export { DrugInputField, DrugInputFieldPreview } from './DrugInputField';

// Re-export output field components
export { DrugOutputField, DrugOutputFieldPreview } from './DrugOutputField';
export { ProcedureOutputField, ProcedureOutputFieldPreview } from './ProcedureOutputField';
export { SiteOfCareOutputField } from './SiteOfCareOutputField';

// =============================================================================
// COMPONENT LOOKUP FUNCTIONS
// =============================================================================

/**
 * Base props interface for input array components (for registry typing)
 */
interface BaseInputProps {
  data: unknown[];
  onChange: (data: unknown[]) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

/**
 * Base props interface for output array components (for registry typing)
 */
interface BaseOutputProps {
  data: unknown[];
  outputMetadata?: FieldMetadata;
  extractedMetadata?: FieldMetadata;
  extractedData?: any;
  onNavigateToCall?: (callId: string, evidenceTimeStart?: string, evidenceTimeEnd?: string) => void;
  onNavigateToAudit?: (auditId: string) => void;
  fieldId?: string;
  isHumanAudit?: boolean;
  onAcceptExtracted?: (fieldPath: string, value: any, fieldLabel: string) => void;
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  currentCallId?: string;
  onChange?: (data: unknown[]) => void;
}

/**
 * Base props interface for preview components (for registry typing)
 */
interface BasePreviewProps {
  data: unknown[];
  maxItems?: number;
}

/**
 * These components are bound to the medbv proto shape, not to a field name:
 * DrugOutputField reads `drug.coverage`, `drug.step_therapy`, `drug.appeal`, …,
 * which only exist on `nomos.ui.medbv.v1`. Registries are therefore keyed by
 * (workflow type, field ID) — a workflow that names a field `drug` without
 * carrying medbv's shape must fall through to generic schema-driven rendering
 * instead of silently inheriting medbv's section layout (BI-1442).
 *
 * To give a workflow custom components, add an entry keyed by its
 * `WorkflowType` value. Registering a field ID globally is deliberately not
 * possible.
 */

const MEDBV_INPUT_COMPONENTS: Record<string, React.ComponentType<BaseInputProps>> = {
  j_code: DrugInputField as React.ComponentType<BaseInputProps>,
  cpt_code: ProcedureInputField as React.ComponentType<BaseInputProps>,
  primary_diagnosis_code: DiagnosisInputField as React.ComponentType<BaseInputProps>,
  sites_of_care: SiteOfCareInputField as React.ComponentType<BaseInputProps>,
};

const MEDBV_OUTPUT_COMPONENTS: Record<string, React.ComponentType<BaseOutputProps>> = {
  drug: DrugOutputField as React.ComponentType<BaseOutputProps>,
  procedure: ProcedureOutputField as React.ComponentType<BaseOutputProps>,
  sites_of_care: SiteOfCareOutputField as React.ComponentType<BaseOutputProps>,
};

/**
 * MEDBHOBV (BV-2328) outputs the medbv entity arrays twice, once per benefit
 * family, under medbv_/bho_-prefixed ids (nomos.ui.medbhobv.v1). Each copy uses
 * the same component as its unprefixed medbv field; callers pass field.id as
 * fieldId, so each copy reads its own data and metadata.
 */
const MEDBHOBV_OUTPUT_COMPONENTS: Record<string, React.ComponentType<BaseOutputProps>> = {
  medbv_drug: DrugOutputField as React.ComponentType<BaseOutputProps>,
  bho_drug: DrugOutputField as React.ComponentType<BaseOutputProps>,
  medbv_procedure: ProcedureOutputField as React.ComponentType<BaseOutputProps>,
  bho_procedure: ProcedureOutputField as React.ComponentType<BaseOutputProps>,
  medbv_sites_of_care: SiteOfCareOutputField as React.ComponentType<BaseOutputProps>,
  bho_sites_of_care: SiteOfCareOutputField as React.ComponentType<BaseOutputProps>,
};

const MEDBV_PREVIEW_COMPONENTS: Record<string, React.ComponentType<BasePreviewProps>> = {
  j_code: DrugInputFieldPreview as React.ComponentType<BasePreviewProps>,
  cpt_code: ProcedureInputFieldPreview as React.ComponentType<BasePreviewProps>,
  primary_diagnosis_code: DiagnosisInputFieldPreview as React.ComponentType<BasePreviewProps>,
  sites_of_care: SiteOfCareOutputFieldPreview as React.ComponentType<BasePreviewProps>,
  drug: DrugOutputFieldPreview as React.ComponentType<BasePreviewProps>,
  procedure: ProcedureOutputFieldPreview as React.ComponentType<BasePreviewProps>,
};

/**
 * PA Status (pa_status_medbv) gets the PREVIEW components only — the compact
 * code/name Badge pills — and deliberately NOT the rich section components above.
 *
 * The BI-1442 rule still holds for those: `DrugOutputField` reads `drug.coverage`,
 * `drug.step_therapy` and friends, which pa_status_medbv does not have. The previews
 * are a different matter — they read `{ code, name }` and nothing else, which
 * pa_status_medbv's `drug[]`/`procedure[]` entries do carry (labelled "J Code" and
 * "CPT Code" in the schema). Without them these columns render as "3 items", since
 * `formatComplexValue` bails out on an array of objects.
 *
 * Note the PLURAL input keys: medbv names its input arrays `j_code`/`cpt_code`, while
 * pa_status_medbv names them `j_codes`/`cpt_codes`.
 */
const PA_STATUS_MEDBV_PREVIEW_COMPONENTS: Record<string, React.ComponentType<BasePreviewProps>> = {
  j_codes: DrugInputFieldPreview as React.ComponentType<BasePreviewProps>,
  cpt_codes: ProcedureInputFieldPreview as React.ComponentType<BasePreviewProps>,
  drug: DrugOutputFieldPreview as React.ComponentType<BasePreviewProps>,
  procedure: ProcedureOutputFieldPreview as React.ComponentType<BasePreviewProps>,
};

/**
 * Input component registry - the source of truth for input components,
 * keyed by workflow type then field ID.
 */
const INPUT_COMPONENTS: Record<string, Record<string, React.ComponentType<BaseInputProps>>> = {
  [WorkflowType.MEDBV]: MEDBV_INPUT_COMPONENTS,
  [WorkflowType.UNIFIEDMEDBV]: MEDBV_INPUT_COMPONENTS,
  [WorkflowType.BHO]: MEDBV_INPUT_COMPONENTS,
  [WorkflowType.MEDBHOBV]: MEDBV_INPUT_COMPONENTS,
};

/**
 * Output component registry - the source of truth for output components,
 * keyed by workflow type then field ID.
 */
const OUTPUT_COMPONENTS: Record<string, Record<string, React.ComponentType<BaseOutputProps>>> = {
  [WorkflowType.MEDBV]: MEDBV_OUTPUT_COMPONENTS,
  [WorkflowType.UNIFIEDMEDBV]: MEDBV_OUTPUT_COMPONENTS,
  [WorkflowType.BHO]: MEDBV_OUTPUT_COMPONENTS,
  [WorkflowType.MEDBHOBV]: MEDBHOBV_OUTPUT_COMPONENTS,
};

/**
 * Preview component registry - the source of truth for preview components,
 * keyed by workflow type then field ID.
 */
const PREVIEW_COMPONENTS: Record<string, Record<string, React.ComponentType<BasePreviewProps>>> = {
  [WorkflowType.MEDBV]: MEDBV_PREVIEW_COMPONENTS,
  [WorkflowType.UNIFIEDMEDBV]: MEDBV_PREVIEW_COMPONENTS,
  [WorkflowType.BHO]: MEDBV_PREVIEW_COMPONENTS,
  [WorkflowType.MEDBHOBV]: MEDBV_PREVIEW_COMPONENTS,
  [WorkflowType.PA_STATUS_MEDBV]: PA_STATUS_MEDBV_PREVIEW_COMPONENTS,
};

/**
 * Get the input component for a given field ID within a workflow.
 * Returns undefined if the workflow has no custom input component for this field.
 *
 * @param fieldId - The field ID (e.g., 'j_code', 'cpt_code')
 * @param workflowType - The case's workflow type (e.g., 'medbv')
 * @returns The input component or undefined if not found
 */
export function getInputComponent(
  fieldId: string,
  workflowType: string | undefined,
): React.ComponentType<BaseInputProps> | undefined {
  if (!workflowType) return undefined;
  return INPUT_COMPONENTS[workflowType]?.[fieldId];
}

/**
 * Get the output component for a given field ID within a workflow.
 * Returns undefined if the workflow has no custom output component for this field.
 *
 * @param fieldId - The field ID (e.g., 'drug', 'procedure')
 * @param workflowType - The case's workflow type (e.g., 'medbv')
 * @returns The output component or undefined if not found
 */
export function getOutputComponent(
  fieldId: string,
  workflowType: string | undefined,
): React.ComponentType<BaseOutputProps> | undefined {
  if (!workflowType) return undefined;
  return OUTPUT_COMPONENTS[workflowType]?.[fieldId];
}

/**
 * Get the preview component for a given field ID within a workflow.
 * Returns undefined if the workflow has no custom preview component for this field.
 *
 * @param fieldId - The field ID (e.g., 'j_code', 'drug')
 * @param workflowType - The case's workflow type (e.g., 'medbv')
 * @returns The preview component or undefined if not found
 */
export function getPreviewComponent(
  fieldId: string,
  workflowType: string | undefined,
): React.ComponentType<BasePreviewProps> | undefined {
  if (!workflowType) return undefined;
  return PREVIEW_COMPONENTS[workflowType]?.[fieldId];
}
